import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  AXIOS_INSTANCE,
  grantRoleToUser,
  useFetchGrantedRolesToUser
} from '@jobandtalent/auth-api-client-v2';
import { FetchRoles200, Role } from '@jobandtalent/auth-api-client-v2/dist/apiClient/model';
import { Select, Textarea } from '@jobandtalent/design-system';

import useSWRInfinite from 'swr/infinite';

import { formatDataScopesData, formatDataScopesInput } from 'src/common/dataScopesHelper';
import FormInput from 'src/components/FormInput/FormInput';
import StackView, { SaveCancelFooter } from 'src/components/StackView/StackView';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';

import { NewRoleFormProps, RoleOption, TextareaValue } from '../Users.types';

const formatContent = (content: string): string => {
  return content.replace(/, */g, ',\n');
};

const calculateHeight = (displayValue: string, defaultHeight: string): string => {
  const textarea = document.createElement('textarea');
  textarea.style.visibility = 'hidden';
  textarea.style.position = 'absolute';
  textarea.style.height = 'auto';
  textarea.style.whiteSpace = 'pre';
  textarea.value = displayValue;

  document.body.appendChild(textarea);
  const calculatedHeight = Math.max(textarea.scrollHeight, parseInt(defaultHeight));
  document.body.removeChild(textarea);

  return `${calculatedHeight}px`;
};

const fetcher = (input: RequestInfo): Promise<FetchRoles200> => {
  return AXIOS_INSTANCE({ url: input as string, method: 'GET' }).then((res) => res.data);
};

const getKey = (pageIndex: number, previousPageData: FetchRoles200) => {
  // reached the end
  if (previousPageData && !previousPageData.meta.next_page === null) {
    return null;
  }

  return `/roles?page=${pageIndex + 1}&per_page=100`; // SWR key
};

interface KeyValuePair {
  key: string;
  value: string;
}

const parseDataScopesInput = (input: string): KeyValuePair[] => {
  return input.split(',').map(pair => {
    const [key, value] = pair.split(':').map(s => s.trim());
    return { key, value };
  });
};

const NewRoleForm = ({ onClose, onSuccess, selectedUser, selectedRole }: NewRoleFormProps) => {
  const {
    data: rolesData,
    error: rolesError,
    size,
    setSize
  } = useSWRInfinite<FetchRoles200>(getKey, fetcher, {
    revalidateFirstPage: false
  });
  const { data: userRolesData } = useFetchGrantedRolesToUser(selectedUser.id);
  const { error, setError } = useError();

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const { permissions, error: currentUserPermissionError } = useLocalPermissions();
  const companyAdminPermission = !currentUserPermissionError && permissions.companyAdminPermission;

  const initialDataScope = selectedRole ? formatDataScopesInput(selectedRole.data_scope) : '';
  const [dataPairs, setDataPairs] = useState<KeyValuePair[]>([]);
  const [dataScopeValue, setDataScopeValue] = useState<string>(initialDataScope);
  const [shouldRenderDataPairs, setShouldRenderDataPairs] = useState(false);

  useEffect(() => {
    if (rolesData) {
      // if there's still something to fetch, fetch it before setting select options
      if (rolesData.at(-1)?.meta.next_page !== null) {
        setSize(size + 1);
      } else {
        let roles = rolesData
          .reduce((acc: Role[], rolesPage) => [...acc, ...rolesPage.data.roles], [])
          .filter(
            ({ id }) => selectedRole?.role_id === id || !userRolesData?.data.user_roles.find(({ role_id }) => role_id === id)
          );

        if (!companyAdminPermission) {
          roles = roles.filter(role => !role.app);
        }

        const isCompanyUser = selectedUser.company_users && selectedUser.company_users.length > 0;
        if (!isCompanyUser) {
          roles = roles.filter(role => !role.app);
        }

        setRoleOptions(roles.map(({ id, name, app }) => ({ value: id, label: app ? `${name} (Company App)` : name, app })));
      }
    }
  }, [rolesData, userRolesData, companyAdminPermission, selectedRole, selectedUser.company_users]);

  useEffect(() => {
    if (rolesError) {
      setError(rolesError);
    }
  }, [rolesError]);

  const handleTextareaChange = (value: string) => {
    setDataPairs(parseDataScopesInput(value));
  };

  const handlePairChange = (index: number, keyOrValue: 'key' | 'value', newValue: string) => {
    const newData = dataPairs.map((pair, i) => {
      if (i === index) {
        return { ...pair, [keyOrValue]: newValue };
      }
      return pair;
    });
    setDataPairs(newData);
    setDataScopeValue(newData.map(pair => `${pair.key}:${pair.value}`).join(','));
  };

  const onSubmit = async (data: { roleId: RoleOption; dataScopes: TextareaValue }) => {
    if (selectedRole) {
      AXIOS_INSTANCE({
        url: `/users/${selectedUser.id}/roles/${selectedRole.role_id}`,
        method: 'PATCH',
        data: { user_role: { data_scope: formatDataScopesData(data.dataScopes.value) } }
      }).then(onSuccess, (error: { response?: { data?: { message?: string } } }) => {
        setError(error?.response?.data?.message ? (error.response.data as { message: string }) : DEFAULT_ERROR);
      });
    } else {
      grantRoleToUser(selectedUser.id, {
        user_role: userRoleData(data)
      }).then(onSuccess, (error) => {
        setError(error?.response?.data?.message ? error?.response?.data : DEFAULT_ERROR);
      });
    }
  };

  type RoleFormDataTypeWithoutDataScopes = {
    role_id: number;
  };

  type RoleFormDataTypeWithDataScopes = {
    role_id: number;
    data_scope: string[];
  };

  const userRoleData = (data: { roleId: RoleOption; dataScopes: TextareaValue }): RoleFormDataTypeWithDataScopes | RoleFormDataTypeWithoutDataScopes => {
    return {
      role_id: data.roleId.value,
      data_scope: formatDataScopesData(data.dataScopes.value)
    }


    return {
      role_id: data.roleId.value
    }
  }

  const { control, handleSubmit } = useForm<{ roleId: RoleOption; dataScopes: TextareaValue; }>();

  const StackViewFooter = <SaveCancelFooter onClose={onClose} onSelect={handleSubmit(onSubmit)} />;

  const keyValuePattern = /\w+:\w+/;

  return (
    <StackView footer={StackViewFooter} onClose={onClose} nested={true} title={selectedRole ? 'Edit role' : 'Grant new role'}>
      <FormInput label="Role name">
        <Controller
          name="roleId"
          control={control}
          render={({ field: { onChange } }) => (
            <Select
              onChange={onChange}
              options={roleOptions}
              isDisabled={!!selectedRole}
              value={
                selectedRole
                  ? roleOptions.find((option) => option.value === selectedRole.role_id)
                  : undefined
              }
            />
          )}
        />
      </FormInput>
      <Controller
        name="dataScopes"
        control={control}
        render={({ field: { onChange } }) => (
          <div>
            <label htmlFor="data" className="ds-text-body-base-semibold">Data Scopes</label>
            {shouldRenderDataPairs && dataPairs.map((pair, index) => (
              <div className="data-scope-block" key={index}>
                <input
                  name="data"
                  type="text"
                  value={pair.key}
                  onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                />
                <input
                  type="text"
                  value={pair.value}
                  onChange={(e) => handlePairChange(index, 'value', e.target.value)}
                />
              </div>
            ))}
            <Textarea
              name="data-scope"
              height={calculateHeight(formatContent(dataScopeValue), "87")}
              onChange={(e) => {
                handleTextareaChange(e.value);
                onChange(e);
                if (keyValuePattern.test(e.value)) {
                  setShouldRenderDataPairs(true);
                } else {
                  setShouldRenderDataPairs(false);
                }
              }}
              value={formatContent(dataScopeValue ? dataScopeValue : initialDataScope)}
              placeholder="Data scopes must be separated by commas. i.e., company_id:1, company_id:2, country:ES"
            />
          </div>
        )}
      />
      {error}
    </StackView>
  );
};

export default NewRoleForm;
